import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useCurrentUser } from '@hooks';
import { FullScreenSpin, FullScreenWrapper } from '@components';
import { Button, Result } from 'antd';
import { ICurrentUserBaasClient } from '@entities';
import { navigateToLogout } from '../AxiosClientProvider/navigateToLogin';

export let SelectedClientContext = React.createContext({
  onChange: (clientId: string) => {},
  currentClient: undefined,
  allClients: [],
  isLoading: false,
} as {
  onChange: (clientId: string) => void;
  currentClient: ICurrentUserBaasClient | undefined;
  allClients: ICurrentUserBaasClient[];
  isLoading: boolean;
});

export function SelectedClientProvider({
  children,
}: PropsWithChildren<unknown>) {
  let { data, isLoading } = useCurrentUser();
  let [currentClient, setCurrentClient] = useState<
    ICurrentUserBaasClient | undefined
  >(undefined);

  let handleSetClient = useCallback(
    (clientId: string) => {
      setCurrentClient(
        data?.baasUserRoles?.find((role) => role?.baasClient?.id === clientId)
          ?.baasClient,
      );
    },
    [data?.baasUserRoles],
  );

  let handleSetByDefault = useCallback(() => {
    if (!currentClient) {
      setCurrentClient(data?.baasUserRoles?.[0]?.baasClient);
    }
  }, [currentClient, data?.baasUserRoles]);

  let availableClients = useMemo(
    () =>
      data?.baasUserRoles
        ?.filter((role) => Boolean(role.baasClient))
        .map((role) => role.baasClient) ?? [],
    [data?.baasUserRoles],
  );

  useEffect(() => {
    handleSetByDefault();
  }, [data]);

  return (
    <SelectedClientContext.Provider
      value={{
        onChange: handleSetClient,
        currentClient,
        allClients: availableClients,
        isLoading,
      }}
    >
      {!currentClient && isLoading && (
        <FullScreenSpin description="Loading your clients" />
      )}
      {currentClient && children}
      {!currentClient && data?.baasUserRoles?.length === 0 && (
        <FullScreenWrapper>
          <Result
            status="403"
            title="403"
            subTitle="Sorry, you’re trying to access some places you don’t have permissions to go."
            extra={
              <Button type="primary" onClick={() => navigateToLogout()}>
                Log out
              </Button>
            }
          />
        </FullScreenWrapper>
      )}
    </SelectedClientContext.Provider>
  );
}
